import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircleFilled, CopyOutlined, LeftOutlined } from "@ant-design/icons";
import { hideSupplierBrandFromUserText } from "./hideSupplierBrandFromUserText";
import { FaBed } from "react-icons/fa";
import { message, Skeleton } from "antd";
import { HOTELSRIVER_OFFICIAL_LOGO_URL } from "../../utils/hotelsRiverBrandLogo";
import {
  formatHubwayzApiError,
  prebookHubwayzHotel,
} from "../../services/hubwayzHotelsApi";
import {
  readMockPassengersClipboard,
  writeMockPassengersClipboard,
} from "../packageProfileMockup/mockPackageProfileData";
import { displayBoardBasisHe } from "./displayBoardBasisHe";
import HubwayzPrebookConfirmPanel, {
  RoomConfirmModal,
} from "./hubwayz/HubwayzPrebookConfirmPanel";
import { roomConfirmState } from "./hubwayz/roomConfirmState";
import {
  hotelDeferredPaymentAvailable,
  passengerContinueGate,
} from "./hubwayz/hotelPassengerCheckout";
import { continueHubwayzHotelToPayment } from "./hubwayz/continueHubwayzHotelToPayment";
import { normalizeHubwayzPrebook } from "./hubwayz/normalizeHubwayzPrebook";
import { isHubwayzHotelsOnlyDraft } from "./hubwayzHotelsOnlyDraft";
import {
  computeSupplementSelectionExtrasTotal,
  mapRawLdsSupplementsToMockupExtras,
} from "../../Components/FlightBookingFlowMockup/adapters/flightOfferAdapter";
import {
  shouldRequirePassportOnAgentFlightForm,
  getFlightPassengerPaxValidationMessage,
} from "../../Components/FlightBookingFlowMockup/flightBookingMockup/flightPassengersFormShared";
import AutoFormatBirthDatePicker from "../../Components/Inputs/AutoFormatBirthDatePicker";
import { getEnglishOnlyPersonNameError } from "../../utils/passengers/englishOnlyPersonName";
import { computeSupplementPriceIls } from "../../utils/lds/baggageSupplementPricing";
import { useAppContext } from "../../contexts/AppContext";
import { FLIGHTS_MODULE_PROPOSAL_ONLY_BODY_KEY } from "../../config/flightsModuleLds";
import {
  buildEmptyFlightPassengers,
  buildLdsProposalOnlyFlightSlice,
  mapPrimaryContactToHubwayzCustomer,
  mapPrimaryContactToHubwayzLead,
  roomInfoFromAppliedSearch,
} from "./buildUnifiedPaxAndHubwayzLead";
import UnifiedHpmBaggagePurchase from "./UnifiedHpmBaggagePurchase";
import { useHubwayzHotelSaleCommission } from "./hubwayz/useHubwayzHotelSaleCommission";
import { useHubwayzVerifiedQuotes } from "./hubwayz/useHubwayzVerifiedQuotes";
import { hubwayzQuoteBlocksPayment } from "./hubwayz/hubwayzVerifiedQuote";
import { customerPriceWithAddedCommission } from "./hubwayz/hubwayzQuoteAddedCommission";
import HubwayzRoomAgentTools from "./hubwayz/HubwayzRoomAgentTools";
import { hubwayzFxBlocksPayment } from "./hubwayz/resolveHubwayzHotelIlsPricing";
import UnifiedFlightHotelOrderSummary from "./UnifiedFlightHotelOrderSummary";
import {
  buildUnifiedBaggageDetailItems,
  buildUnifiedBaggageDetailLabel,
} from "./buildUnifiedBaggageDetailLabel";
import { resolveUnifiedFlightLegs } from "./unifiedFlightLegs";
import BookingStageNav from "./BookingStageNav";
import "../packageProfileMockup/PackageProfileMockupPage.css";
import "./FlightHotelPassengersPage.css";

const EMPTY_EXTRAS = {
  baggage: [],
  supplementUnitFares: {},
  supplementUnitFaresIls: {},
  supplementPaxSelections: {},
};

function quoteWithoutAddedCommission(quote) {
  const extra = Number(quote?.addedCommissionIls) || 0;
  if (!quote || extra <= 0) return quote;
  const sale = Number(quote.customerSaleIls ?? quote.customerPayIls);
  if (!Number.isFinite(sale)) return quote;
  const base = Math.max(0, Math.round(sale - extra));
  return {
    ...quote,
    customerSaleIls: base,
    customerPayIls: base,
    addedCommissionIls: 0,
  };
}

function formatStayDate(raw) {
  const s = String(raw || "").trim();
  if (!s) return "";
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  return s;
}

function roomGuestsLabel(room) {
  const adults = Number(room?.adults) || 0;
  const children = (Number(room?.kids) || 0) + (Number(room?.infants) || 0);
  const adultText = adults === 1 ? "מבוגר אחד" : `${adults} מבוגרים`;
  if (!children) return adultText;
  const childText = children === 1 ? "ילד אחד" : `${children} ילדים`;
  return `${adultText}, ${childText}`;
}

function hotelGuestGroups(passengers, rooms) {
  const groups = [];
  let cursor = 0;
  const source = rooms?.length
    ? rooms
    : [{ adults: passengers.length, kids: 0, infants: 0 }];
  source.forEach((room) => {
    const count =
      (Number(room.adults) || 0) +
      (Number(room.kids) || 0) +
      (Number(room.infants) || 0);
    const slice = passengers.slice(cursor, cursor + Math.max(0, count));
    groups.push({
      room,
      guests: slice.map((p, offset) => ({ p, idx: cursor + offset })),
    });
    cursor += count;
  });
  if (cursor < passengers.length) {
    groups.push({
      room: null,
      guests: passengers.slice(cursor).map((p, offset) => ({ p, idx: cursor + offset })),
    });
  }
  return groups.filter((group) => group.guests.length);
}

const HOTEL_GUEST_TERMS_URL = "https://www.hotelsriver.com/termsAndConditions";
const HOTEL_GUEST_DISCLOSURE = [
  "שינוי או ביטול הזמנה כרוכים בדמי טיפול של החברה בסך 85$ לנוסע, בנוסף לדמי הביטול של הספק.",
  "המחיר והזמינות סופיים רק לאחר אישור הספק ושובר.",
  "תנאי הביטול והשינוי נקבעים בלעדית על ידי הספק ועלולים לכלול חיובים מלאים.",
  "ייתכנו אי־דיוקים במידע ובתמונות.",
  "הסוכן מחויב להעביר ללקוח את כל תנאי ההזמנה בצורה מלאה וברורה.",
];

function roomsPayloadFromSearch(appliedSearch, paxFallback) {
  const rooms = Array.isArray(appliedSearch?.rooms) ? appliedSearch.rooms : [];
  if (rooms.length) {
    return rooms.map((r) => {
      const adultsRaw = r?.adults ?? r?.AdultsCount ?? r?.Adults;
      const adultsNum = Number(adultsRaw);
      const adults =
        Number.isFinite(adultsNum) && adultsNum >= 1 ? adultsNum : 1;
      const agesRaw = r?.childAges ?? r?.KidsAges ?? r?.kidsAges ?? [];
      return {
        adults,
        childAges: Array.isArray(agesRaw) ? agesRaw : [],
      };
    });
  }
  const fallback = Number(paxFallback);
  return [
    {
      adults: Number.isFinite(fallback) && fallback >= 1 ? fallback : 2,
      childAges: [],
    },
  ];
}

function CollapsibleSection({ id, title, subtitle, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="hpm-section" id={id} data-section={id}>
      <button
        type="button"
        className="hpm-section__head"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <div>
          <h2>{title}</h2>
          {subtitle ? <p>{subtitle}</p> : null}
        </div>
        <span className="hpm-section__chev" aria-hidden>
          {open ? "−" : "+"}
        </span>
      </button>
      {open ? <div className="hpm-section__body">{children}</div> : null}
    </section>
  );
}

/**
 * Passengers + baggage — visual SSOT = packageProfileMockup (HPM).
 * LDS PROPOSAL-only + Hubwayz prebook stay under the hood.
 */
function isHubwayzSearchExpiredError(msg) {
  return /a414|Could not find search|run a new search|קוד ההצעה פג/i.test(
    String(msg || "")
  );
}

export default function FlightHotelPassengersPage({
  bookingDraft,
  appliedSearch,
  onBack,
  onResults,
  onExpiredSearch,
  onContinueToPayment,
  deferredPaymentSystemAllowed = false,
}) {
  const paxCount = Math.max(1, Number(bookingDraft?.paxCount) || 2);
  const rawDeal = bookingDraft?.rawDeal || null;
  const roomInfo = useMemo(
    () => roomInfoFromAppliedSearch(appliedSearch, paxCount),
    [appliedSearch, paxCount]
  );
  const {
    currencyRate,
    currencyRateSource,
    globalConstant,
    gcKey,
    getBaggageFeeFlat,
    getHrCommissionPercent,
    getHrCommissionPercentHotels,
    getAgentCommissionPercent,
    getCreditCardFeePercent,
  } = useAppContext();
  const hrCommissionHotelsSetting = globalConstant?.[gcKey.HR_COMMISSION_HOTELS];
  const agentSaleCommission = useHubwayzHotelSaleCommission();
  const currencyRateUsd = Number(currencyRate?.USD) || 0;
  const baggageFeeFlatUsd =
    typeof getBaggageFeeFlat === "function" ? getBaggageFeeFlat() : 0;

  const [passengers, setPassengers] = useState(() =>
    buildEmptyFlightPassengers(appliedSearch, paxCount)
  );
  const [extras, setExtras] = useState(EMPTY_EXTRAS);
  const navigate = useNavigate();
  const [toast, setToast] = useState("");
  const [payBusy, setPayBusy] = useState(false);
  const [prebookLoading, setPrebookLoading] = useState(false);
  const [prebookError, setPrebookError] = useState(null);
  const [prebookResult, setPrebookResult] = useState(null);
  const prebookSeqRef = useRef(0);
  const [proposalChecking, setProposalChecking] = useState(false);
  const [proposalError, setProposalError] = useState(null);
  const [hasClipboard, setHasClipboard] = useState(
    () => !!readMockPassengersClipboard()?.passengers?.length
  );
  const [termsChecked, setTermsChecked] = useState(false);
  const [hotelNotes, setHotelNotes] = useState(() => String(bookingDraft?.hotelNotes || ""));
  const [priceChangeAck, setPriceChangeAck] = useState(false);

  const isLive =
    bookingDraft?.source === "live" &&
    !!bookingDraft?.hubwayzOfferCode &&
    !!bookingDraft?.searchRequest;

  /** Hotels-tab / Hubwayz-only: no LDS flight slice — never require rawDeal. */
  const hotelsOnly = isHubwayzHotelsOnlyDraft(bookingDraft);
  const bookingProduct = hotelsOnly ? "hotel" : "flightHotel";
  const [addedCommissionIls, setAddedCommissionIls] = useState(() =>
    bookingDraft?.agentCommissionType ? 0 : Number(bookingDraft?.addedCommissionIls) || 0
  );
  const [lockedCommissionQuote, setLockedCommissionQuote] = useState(null);
  const quoteClaims = useMemo(() => {
    if (!hotelsOnly) return [];
    const offerCode = bookingDraft?.hubwayzOfferCode;
    const searchId = bookingDraft?.hubwayzSearchId || bookingDraft?.searchId;
    if (!offerCode || !searchId) return [];
    const claim = {
      offerCode,
      searchId,
      displayCurrency: bookingDraft?.displayCurrency || bookingDraft?.currency || null,
    };
    if (bookingDraft?.agentCommissionType) {
      claim.commissionType = bookingDraft.agentCommissionType;
      claim.commissionValue = bookingDraft.agentCommissionValue;
    }
    return [claim];
  }, [
    hotelsOnly,
    bookingDraft?.hubwayzOfferCode,
    bookingDraft?.hubwayzSearchId,
    bookingDraft?.searchId,
    bookingDraft?.displayCurrency,
    bookingDraft?.currency,
    bookingDraft?.agentCommissionType,
    bookingDraft?.agentCommissionValue,
  ]);
  const passengerQuotes = useHubwayzVerifiedQuotes({
    enabled: hotelsOnly && quoteClaims.length > 0,
    claims: quoteClaims,
    refreshKey: `${agentSaleCommission?.type || "percentage"}:${agentSaleCommission?.value ?? 0}`,
  });
  const seededQuote =
    bookingDraft?.hubwayzVerifiedQuote?.source === "server_quote"
      ? bookingDraft.hubwayzVerifiedQuote
      : null;
  const liveQuote = passengerQuotes.byCode[String(bookingDraft?.hubwayzOfferCode || "")];
  const baseQuote = quoteWithoutAddedCommission(liveQuote || seededQuote);
  const customerPrice = hotelsOnly
    ? lockedCommissionQuote?.binding
      ? lockedCommissionQuote
      : customerPriceWithAddedCommission(baseQuote, addedCommissionIls)
    : null;
  const quoteBlocked =
    isLive &&
    hotelsOnly &&
    !customerPrice?.binding &&
    (passengerQuotes.loading || hubwayzQuoteBlocksPayment(customerPrice));
  const fxBlocked = hotelsOnly ? false : hubwayzFxBlocksPayment(customerPrice);

  const currency = bookingDraft?.currency || "USD";
  const requirePassport = hotelsOnly
    ? false
    : shouldRequirePassportOnAgentFlightForm(rawDeal);
  const returnDate =
    appliedSearch?.checkOut || bookingDraft?.dateRangeLabel || undefined;
  const summaryFlightLegs = useMemo(
    () =>
      resolveUnifiedFlightLegs({
        outbound: bookingDraft?.outbound,
        inbound: bookingDraft?.inbound,
        rawDeal: bookingDraft?.rawDeal,
      }),
    [bookingDraft?.outbound, bookingDraft?.inbound, bookingDraft?.rawDeal]
  );

  useEffect(() => {
    setPassengers(buildEmptyFlightPassengers(appliedSearch, paxCount));
    setExtras(EMPTY_EXTRAS);
    setTermsChecked(false);
    // Depend on search identity primitives — not appliedSearch object reference
    // (recreated parent renders would wipe filled passenger forms).
  }, [
    paxCount,
    bookingDraft?.packageId,
    bookingDraft?.flightId,
    appliedSearch?.checkIn,
    appliedSearch?.checkOut,
    JSON.stringify(appliedSearch?.rooms || []),
  ]);

  useEffect(() => {
    if (!rawDeal) return;
    const rows = mapRawLdsSupplementsToMockupExtras(rawDeal).filter(
      (r) => Number(r?.fareAmount) > 0
    );
    setExtras((prev) => {
      const validKeys = new Set(rows.map((r) => r.supplementKey));
      const supplementUnitFares = {};
      const supplementUnitFaresIls = {};
      for (const row of rows) {
        supplementUnitFares[row.supplementKey] = row.fareAmount;
        supplementUnitFaresIls[row.supplementKey] = computeSupplementPriceIls({
          fareUsd: row.fareAmount,
          currencyRateUsd,
          baggageFeeFlatUsd,
          supplement: row,
        });
      }
      const supplementPaxSelections = { ...prev.supplementPaxSelections };
      for (const k of Object.keys(supplementPaxSelections)) {
        if (!validKeys.has(k)) delete supplementPaxSelections[k];
      }
      return {
        ...prev,
        supplementUnitFares,
        supplementUnitFaresIls,
        supplementPaxSelections,
      };
    });
  }, [rawDeal, currencyRateUsd, baggageFeeFlatUsd]);

  const runPrebook = useCallback(async () => {
    if (!isLive) return false;
    const seq = ++prebookSeqRef.current;
    setPrebookLoading(true);
    setPrebookError(null);
    setPrebookResult(null);
    try {
      const res = await prebookHubwayzHotel({
        searchRequest: bookingDraft.searchRequest,
        searchId:
          bookingDraft.hubwayzSearchId ||
          bookingDraft.hubwayzVerifiedQuote?.searchId ||
          null,
        code: bookingDraft.hubwayzOfferCode,
        rooms: roomsPayloadFromSearch(appliedSearch, paxCount),
      });
      if (seq !== prebookSeqRef.current) return false;
      if (!res?.ok) {
        const msg = res?.error || "prebook failed";
        const hint = /run a new search|a414|Could not find search/i.test(msg)
          ? " · חזרו לתוצאות וחפשו מחדש (קוד ההצעה פג)"
          : "";
        setPrebookError(`${msg}${hint}`);
        setPrebookResult(null);
        return false;
      }
      setPrebookResult(res.data || null);
      return true;
    } catch (err) {
      if (seq !== prebookSeqRef.current) return false;
      setPrebookError(formatHubwayzApiError(err));
      setPrebookResult(null);
      return false;
    } finally {
      if (seq === prebookSeqRef.current) setPrebookLoading(false);
    }
  }, [
    isLive,
    bookingDraft?.searchRequest,
    bookingDraft?.hubwayzSearchId,
    bookingDraft?.hubwayzVerifiedQuote?.searchId,
    bookingDraft?.hubwayzOfferCode,
    appliedSearch,
    paxCount,
  ]);

  useEffect(() => {
    if (!isLive) return undefined;
    const seq = ++prebookSeqRef.current;
    setPrebookLoading(true);
    setPrebookError(null);
    setPrebookResult(null);
    (async () => {
      try {
        const res = await prebookHubwayzHotel({
          searchRequest: bookingDraft.searchRequest,
          searchId:
            bookingDraft.hubwayzSearchId ||
            bookingDraft.hubwayzVerifiedQuote?.searchId ||
            null,
          code: bookingDraft.hubwayzOfferCode,
          rooms: roomsPayloadFromSearch(appliedSearch, paxCount),
        });
        if (seq !== prebookSeqRef.current) return;
        if (!res?.ok) {
          const msg = res?.error || "prebook failed";
          const hint = /run a new search|a414|Could not find search/i.test(msg)
            ? " · חזרו לתוצאות וחפשו מחדש (קוד ההצעה פג)"
            : "";
          setPrebookError(`${msg}${hint}`);
          setPrebookResult(null);
          return;
        }
        setPrebookResult(res.data || null);
      } catch (err) {
        if (seq !== prebookSeqRef.current) return;
        setPrebookError(formatHubwayzApiError(err));
        setPrebookResult(null);
      } finally {
        if (seq === prebookSeqRef.current) setPrebookLoading(false);
      }
    })();
    return () => {
      // Invalidate in-flight responses when deps change / unmount.
      prebookSeqRef.current += 1;
    };
  }, [
    isLive,
    bookingDraft?.hubwayzOfferCode,
    bookingDraft?.hubwayzSearchId,
    bookingDraft?.hubwayzVerifiedQuote?.searchId,
    bookingDraft?.searchRequest,
    bookingDraft?.packageId,
    appliedSearch,
    paxCount,
  ]);

  const handleTogglePax = useCallback((supplementKey, paxId, checked) => {
    setExtras((prev) => {
      const cur = new Set(prev.supplementPaxSelections[supplementKey] || []);
      if (checked) cur.add(paxId);
      else cur.delete(paxId);
      const nextArr = Array.from(cur);
      const supplementPaxSelections = { ...prev.supplementPaxSelections };
      if (nextArr.length === 0) delete supplementPaxSelections[supplementKey];
      else supplementPaxSelections[supplementKey] = nextArr;
      return { ...prev, supplementPaxSelections };
    });
  }, []);

  const baggageExtrasUsd = useMemo(
    () =>
      computeSupplementSelectionExtrasTotal(
        extras.supplementPaxSelections,
        extras.supplementUnitFares
      ),
    [extras.supplementPaxSelections, extras.supplementUnitFares]
  );

  const baggageDetailItems = useMemo(
    () => buildUnifiedBaggageDetailItems({ extras, rawDeal }),
    [extras, rawDeal]
  );
  const baggageDetailLabel = useMemo(
    () =>
      buildUnifiedBaggageDetailLabel({
        extras,
        rawDeal,
      }),
    [extras, rawDeal]
  );

  const prebookNormalized = useMemo(() => {
    if (!prebookResult) return null;
    return normalizeHubwayzPrebook(prebookResult);
  }, [prebookResult]);
  const roomConfirm = roomConfirmState({
    prebookLoading,
    prebookError,
    normalized: prebookNormalized,
    quoteLoading: passengerQuotes.loading,
    quote: customerPrice,
    quoteEnabled: quoteClaims.length > 0,
  });
  const awaitingRoomConfirm = isLive && hotelsOnly && !roomConfirm.approved;

  const pricing = useMemo(() => {
    const pax = paxCount;
    const bagPer = baggageExtrasUsd > 0 ? baggageExtrasUsd / pax : 0;
    const d = prebookNormalized?.display;
    if (d && (d.netPrice || d.price)) {
      const net = Number(d.netPrice?.amount);
      const gross = Number(d.price?.amount);
      const total =
        Number.isFinite(net) && net > 0
          ? net
          : Number.isFinite(gross) && gross > 0
            ? gross
            : 0;
      const cur =
        d.netPrice?.currency || d.price?.currency || currency;
      const flightPer = Number(bookingDraft?.flightPricePerPerson) || 0;
      const hotelPer = total > 0 ? total / pax : 0;
      const perFx =
        hotelPer +
        flightPer +
        bagPer +
        (Number(bookingDraft?.hrCommissionPerPerson) || 0);
      return {
        pax,
        perFx,
        totalFx: perFx * pax,
        baggageTotalFx: baggageExtrasUsd,
        currency: cur,
        token: d.token || null,
        hotelTotalFx: total > 0 ? total : null,
        priceFromPrebook: total > 0,
      };
    }
    // Live: never fall back to search draft price as "confirmed".
    if (isLive) {
      return {
        pax,
        perFx: bagPer + (Number(bookingDraft?.flightPricePerPerson) || 0),
        totalFx:
          (bagPer + (Number(bookingDraft?.flightPricePerPerson) || 0)) * pax,
        baggageTotalFx: baggageExtrasUsd,
        currency,
        token: null,
        hotelTotalFx: null,
        priceFromPrebook: false,
      };
    }
    const perFx = (Number(bookingDraft?.perFx) || 0) + bagPer;
    return {
      pax,
      perFx,
      totalFx: perFx * pax,
      baggageTotalFx: baggageExtrasUsd,
      currency,
      token: null,
      hotelTotalFx: null,
      priceFromPrebook: false,
    };
  }, [
    prebookNormalized,
    bookingDraft,
    paxCount,
    currency,
    baggageExtrasUsd,
    isLive,
  ]);

  const roomBadge = isLive
    ? prebookNormalized?.display?.roomSummary ||
      bookingDraft?.roomName ||
      "חדר שנבחר"
    : bookingDraft?.roomName || bookingDraft?.boardLabel || "חדר שנבחר";

  const showToast = (msg) => {
    setToast(msg);
    window.setTimeout(() => setToast(""), 3200);
  };

  const patchPassenger = useCallback((index, field, value) => {
    setPassengers((list) =>
      list.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  }, []);

  const handleCopyPassengers = () => {
    writeMockPassengersClipboard(passengers);
    setHasClipboard(true);
    message.success("פרטי הנוסעים הועתקו. ניתן להדביק בחבילה אחרת.");
  };

  const handlePastePassengers = () => {
    const clip = readMockPassengersClipboard();
    const list = clip?.passengers;
    if (!Array.isArray(list) || !list.length) {
      message.info("אין נתונים שמורים להדבקה. העתק תחילה פרטי נוסעים.");
      return;
    }
    setPassengers((prev) =>
      prev.map((row, i) => {
        const src = list[i];
        if (!src) return row;
        return {
          ...row,
          firstName: src.firstName ?? row.firstName,
          lastName: src.lastName ?? row.lastName,
          gender: src.gender ?? row.gender,
          dateOfBirth: src.dateOfBirth ?? row.dateOfBirth,
          passportNumber: src.passportNumber ?? row.passportNumber,
          passportExpiry: src.passportExpiry ?? row.passportExpiry,
          nationality: src.nationality ?? row.nationality,
          cell: src.cell ?? row.cell,
          email: src.email ?? row.email,
        };
      })
    );
    message.success("פרטי הנוסעים הודבקו.");
  };

  const validate = () => {
    if (!termsChecked) {
      return "יש לאשר את תנאי ההזמנה כדי להמשיך";
    }
    for (let i = 0; i < passengers.length; i++) {
      const p = passengers[i];
      const isChild = p?.role === "child" || p?.role === "infant";
      // Hotels-only: adults do not need DOB; children still required by Hubwayz Book.
      const requireDateOfBirth = hotelsOnly ? isChild : true;
      const paxMsg = getFlightPassengerPaxValidationMessage(p, i, {
        requirePassport,
        requireDateOfBirth,
        requireGender: !hotelsOnly,
      });
      if (paxMsg) return paxMsg;
    }
    if (!mapPrimaryContactToHubwayzLead(passengers)) {
      return "נוסע ראשי חייב לכלול שם מלא ל־Hubwayz lead";
    }
    if (isLive && !prebookLoading) {
      if (prebookError) return prebookError;
      if (!prebookNormalized?.supplierConfirmed) {
        const miss = (prebookNormalized?.criticalMissing || []).join(", ");
        return miss
          ? `Pre-Book חסר שדות קריטיים (${miss}) — לא ניתן להמשיך`
          : "לא התקבל Pre-Book תקין מהספק";
      }
      if (!pricing.token) {
        return "לא התקבל token מ־prebook";
      }
    }
    return null;
  };

  const runLdsProposalOnlyCheck = async () => {
    setProposalChecking(true);
    setProposalError(null);
    try {
      if (!rawDeal && bookingDraft?.source === "live") {
        throw new Error("חסר rawDeal לטיסה — בחרו טיסה מחדש");
      }
      const flights = buildLdsProposalOnlyFlightSlice({
        rawDeal,
        passengers,
        extras,
        dealId: bookingDraft?.flightId,
      });
      if (!flights?.[FLIGHTS_MODULE_PROPOSAL_ONLY_BODY_KEY]) {
        throw new Error("payload לא סומן כ־PROPOSAL בלבד");
      }
      await Promise.resolve();
      return { ok: true, mode: "proposal_only", flights };
    } catch (err) {
      const msg = err?.message || String(err);
      setProposalError(msg);
      return { ok: false, error: msg };
    } finally {
      setProposalChecking(false);
    }
  };

  const showDeferredPay =
    hotelsOnly &&
    hotelDeferredPaymentAvailable({
      normalized: prebookNormalized,
      quote: customerPrice,
      systemAllowed: deferredPaymentSystemAllowed === true,
    });

  const handleContinue = async (paymentIntent = "pay_now") => {
    if (quoteBlocked) {
      showToast(
        customerPrice?.status === "price_changed"
          ? "המחיר השתנה. יש לאשר את המחיר המעודכן לפני המשך."
          : "לא ניתן להמשיך לתשלום בלי אימות מחיר מהשרת."
      );
      return;
    }
    if (fxBlocked) {
      showToast("לא ניתן להמשיך לתשלום: מטבע הספק לא מוכר או שאין שער המרה תקין.");
      return;
    }
    const err = validate();
    if (err) {
      showToast(err);
      return;
    }
    const gate = passengerContinueGate({
      hotelsOnly,
      rawDeal,
      source: bookingDraft?.source,
    });
    if (!gate.ok) {
      showToast(gate.error);
      return;
    }
    if (paymentIntent === "pay_later" && !showDeferredPay) {
      showToast("שריון עם תשלום נדחה אינו זמין להצעה הזו.");
      return;
    }
    let proposal = gate.ldsProposal;
    if (gate.needsFlightProposal) {
      proposal = await runLdsProposalOnlyCheck();
      if (!proposal.ok) {
        showToast(proposal.error || "בדיקת PROPOSAL נכשלה");
        return;
      }
    }
    const hubwayzLead = mapPrimaryContactToHubwayzLead(passengers);
    const hubwayzCustomer = mapPrimaryContactToHubwayzCustomer(passengers);
    const nextDraft = {
      ...bookingDraft,
      hubwayzVerifiedQuote: customerPrice?.binding
        ? customerPrice
        : bookingDraft?.hubwayzVerifiedQuote || null,
      agentCommissionType: customerPrice?.commissionLocked
        ? customerPrice.agentCommissionType
        : bookingDraft?.agentCommissionType || null,
      agentCommissionValue: customerPrice?.commissionLocked
        ? customerPrice.agentCommissionValue
        : bookingDraft?.agentCommissionValue ?? null,
      addedCommissionIls: customerPrice?.commissionLocked ? 0 : addedCommissionIls,
      hotelNotes: hotelNotes.trim(),
      paymentIntent: hotelsOnly ? paymentIntent : bookingDraft?.paymentIntent,
      bookingIntent:
        hotelsOnly && paymentIntent === "pay_later" ? "book_now_pay_later" : null,
      cancellation: prebookNormalized?.display?.cancellation || bookingDraft?.cancellation || null,
      searchRequest: bookingDraft?.searchRequest || null,
      passengers,
      extras,
      hubwayzLead,
      hubwayzCustomer,
      hubwayzBookAdults: [
        hubwayzLead,
        ...passengers.slice(1).map((p) => ({
          title: String(p.gender || "M").toUpperCase() === "F" ? "F" : "M",
          lead: false,
          name: {
            first: String(p.firstName || "").trim(),
            last: String(p.lastName || "").trim(),
          },
        })),
      ],
      ldsProposal: proposal,
      prebook: {
        raw: prebookResult,
        token: pricing.token,
        normalized: prebookNormalized,
        hotel: prebookNormalized?.offers?.[0] || null,
      },
      perFx: pricing.perFx,
      totalFx: pricing.totalFx,
      hotelTotalFx: pricing.hotelTotalFx,
      prebookCurrency: pricing.priceFromPrebook ? pricing.currency : null,
      currency: hotelsOnly
        ? bookingDraft?.displayCurrency || bookingDraft?.currency || pricing.currency
        : pricing.currency,
      displayCurrency: bookingDraft?.displayCurrency || appliedSearch?.currency || null,
      baggageTotalFx: pricing.baggageTotalFx,
      baggageItems: baggageDetailItems,
      baggageLabel: baggageDetailLabel,
    };
    if (hotelsOnly && paymentIntent === "pay_later") {
      showToast("שריון ללא תשלום חסום זמנית עד להחלטה על מדיניות השריון.");
      return;
    }
    if (hotelsOnly && paymentIntent === "pay_now" && bookingDraft?.source === "live") {
      if (payBusy) return;
      setPayBusy(true);
      try {
        const opened = await continueHubwayzHotelToPayment({
          paymentIntent: "pay_now",
          draft: nextDraft,
          prebookNormalized,
          appliedSearch,
          passengers,
        });
        if (!opened.ok || !opened.path) {
          showToast(opened.message || "לא נשמרה הזמנה פנימית. לא נשלחה הזמנה לספק.");
          return;
        }
        navigate(opened.path);
      } finally {
        setPayBusy(false);
      }
      return;
    }
    onContinueToPayment?.(nextDraft);
  };

  const renderPassengerCard = (p, idx, displayNo = idx) => {
    const isPrimary = !!p.isPrimary || idx === 0;
    const isChild = p.role === "child" || p.role === "infant";
    // Hotels booking: no DOB for adult guests; keep for children (supplier Book).
    const showDateOfBirth = hotelsOnly ? isChild : true;
    const firstErr = getEnglishOnlyPersonNameError(p.firstName);
    const lastErr = getEnglishOnlyPersonNameError(p.lastName);
    const minAge = p.role === "infant" ? 0 : p.role === "child" ? 2 : 12;
    const maxAge = p.role === "infant" ? 2 : p.role === "child" ? 12 : 100;
    const guestTitle = isChild
      ? p.role === "infant"
        ? `תינוק מס׳ ${displayNo + 1}`
        : `ילד מס׳ ${displayNo + 1}`
      : hotelsOnly
        ? `אורח מס׳ ${displayNo + 1}`
        : `נוסע מס' ${idx + 1}`;

    if (hotelsOnly) {
      return (
        <div key={p.id}>
          <h3 className="hwz-guest-title">{guestTitle}</h3>
          <div className="hpm-pax__form">
            <div className="hpm-pax__row hpm-pax__row--adult">
              <label className="hpm-pax__field">
                <span>שם פרטי לטינית, כפי בדרכון</span>
                <input
                  value={p.firstName || ""}
                  placeholder="First name"
                  autoComplete="given-name"
                  aria-invalid={!!firstErr}
                  onChange={(e) => patchPassenger(idx, "firstName", e.target.value)}
                />
                {firstErr ? (
                  <span className="hpm-pax__error" role="alert">
                    {firstErr}
                  </span>
                ) : null}
              </label>
              <label className="hpm-pax__field">
                <span>שם משפחה (לטינית, כפי בדרכון)</span>
                <input
                  value={p.lastName || ""}
                  placeholder="Last name"
                  autoComplete="family-name"
                  aria-invalid={!!lastErr}
                  onChange={(e) => patchPassenger(idx, "lastName", e.target.value)}
                />
                {lastErr ? (
                  <span className="hpm-pax__error" role="alert">
                    {lastErr}
                  </span>
                ) : null}
              </label>
            </div>
            {isPrimary ? (
              <div className="hpm-pax__row hpm-pax__row--secondary">
                <label className="hpm-pax__field">
                  <span>טלפון נייד</span>
                  <input
                    value={p.cell || ""}
                    inputMode="tel"
                    autoComplete="tel"
                    dir="ltr"
                    onChange={(e) => patchPassenger(idx, "cell", e.target.value)}
                  />
                </label>
                <label className="hpm-pax__field">
                  <span>כתובת דוא״ל</span>
                  <input
                    type="email"
                    value={p.email || ""}
                    autoComplete="email"
                    dir="ltr"
                    onChange={(e) => patchPassenger(idx, "email", e.target.value)}
                  />
                </label>
              </div>
            ) : null}
            {showDateOfBirth ? (
              <div className="hpm-pax__row hpm-pax__row--dob-only">
                <label className="hpm-pax__field hpm-pax__field--dob">
                  <span>תאריך לידה</span>
                  <div className="hpm-pax__dob-control">
                    <AutoFormatBirthDatePicker
                      pickerKey={`usm-pax-dob-${p.id}`}
                      title="תאריך לידה"
                      returnDate={returnDate}
                      minAge={minAge}
                      maxAge={maxAge}
                      value={p.dateOfBirth}
                      onChange={(v) => patchPassenger(idx, "dateOfBirth", v)}
                    />
                  </div>
                </label>
              </div>
            ) : null}
          </div>
        </div>
      );
    }

    return (
      <div
        key={p.id}
        className={`hpm-pax${isChild ? " hpm-pax--child" : ""}${
          p.role === "infant" ? " hpm-pax--infant" : ""
        }`}
      >
        <div className="hpm-pax__head">
          <h3>{guestTitle}</h3>
          {isPrimary ? (
            <span className="hpm-pax__badge" title="איש קשר ראשי">
              איש קשר ראשי
            </span>
          ) : null}
        </div>
        <div className="hpm-pax__form">
          <div
            className={`hpm-pax__row ${
              isPrimary
                ? "hpm-pax__row--primary-contact"
                : isChild
                  ? "hpm-pax__row--minor"
                  : "hpm-pax__row--adult"
            }`}
          >
            <label className="hpm-pax__field hpm-pax__field--gender">
              <span>מין</span>
              <select
                value={p.gender || ""}
                onChange={(e) => patchPassenger(idx, "gender", e.target.value)}
              >
                <option value="">מין</option>
                <option value="M">זכר</option>
                <option value="F">נקבה</option>
              </select>
            </label>
            <label className="hpm-pax__field">
              <span>שם פרטי לטינית, כפי בדרכון</span>
              <input
                value={p.firstName || ""}
                autoComplete="given-name"
                aria-invalid={!!firstErr}
                onChange={(e) =>
                  patchPassenger(idx, "firstName", e.target.value)
                }
              />
              {firstErr ? (
                <span className="hpm-pax__error" role="alert">
                  {firstErr}
                </span>
              ) : null}
            </label>
            <label className="hpm-pax__field">
              <span>שם משפחה (לטינית, כפי בדרכון)</span>
              <input
                value={p.lastName || ""}
                autoComplete="family-name"
                aria-invalid={!!lastErr}
                onChange={(e) =>
                  patchPassenger(idx, "lastName", e.target.value)
                }
              />
              {lastErr ? (
                <span className="hpm-pax__error" role="alert">
                  {lastErr}
                </span>
              ) : null}
            </label>
            {isPrimary ? (
              <label className="hpm-pax__field">
                <span>טלפון נייד</span>
                <input
                  value={p.cell || ""}
                  inputMode="tel"
                  autoComplete="tel"
                  dir="ltr"
                  onChange={(e) => patchPassenger(idx, "cell", e.target.value)}
                />
              </label>
            ) : null}
          </div>

          {isPrimary ? (
            <div className="hpm-pax__row hpm-pax__row--secondary">
              <label className="hpm-pax__field">
                <span>כתובת דוא&quot;ל</span>
                <input
                  type="email"
                  value={p.email || ""}
                  autoComplete="email"
                  dir="ltr"
                  onChange={(e) =>
                    patchPassenger(idx, "email", e.target.value)
                  }
                />
              </label>
              {showDateOfBirth ? (
                <label className="hpm-pax__field hpm-pax__field--dob">
                  <span>תאריך לידה</span>
                  <div className="hpm-pax__dob-control">
                    <AutoFormatBirthDatePicker
                      pickerKey={`usm-pax-dob-${p.id}`}
                      title="תאריך לידה"
                      returnDate={returnDate}
                      minAge={minAge}
                      maxAge={maxAge}
                      value={p.dateOfBirth}
                      onChange={(v) => patchPassenger(idx, "dateOfBirth", v)}
                    />
                  </div>
                </label>
              ) : null}
            </div>
          ) : showDateOfBirth ? (
            <div className="hpm-pax__row hpm-pax__row--dob-only">
              <label className="hpm-pax__field hpm-pax__field--dob">
                <span>תאריך לידה</span>
                <div className="hpm-pax__dob-control">
                  <AutoFormatBirthDatePicker
                    pickerKey={`usm-pax-dob-${p.id}`}
                    title="תאריך לידה"
                    returnDate={returnDate}
                    minAge={minAge}
                    maxAge={maxAge}
                    value={p.dateOfBirth}
                    onChange={(v) => patchPassenger(idx, "dateOfBirth", v)}
                  />
                </div>
              </label>
            </div>
          ) : null}

          {requirePassport ? (
            <div className="hpm-pax__row hpm-pax__row--secondary">
              <label className="hpm-pax__field">
                <span>מספר דרכון</span>
                <input
                  value={p.passportNumber || ""}
                  onChange={(e) =>
                    patchPassenger(idx, "passportNumber", e.target.value)
                  }
                />
              </label>
              <label className="hpm-pax__field">
                <span>תוקף דרכון (DD/MM/YYYY)</span>
                <input
                  value={p.passportExpiry || ""}
                  placeholder="DD/MM/YYYY"
                  onChange={(e) =>
                    patchPassenger(idx, "passportExpiry", e.target.value)
                  }
                />
              </label>
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <div
      className={`hpm-mock usm-fh-pax${hotelsOnly ? " usm-fh-pax--hotel" : ""}`}
      dir="rtl"
      lang="he"
    >
      <header className="hpm-topbar">
        <div className="hpm-topbar__brand">
          <img src={HOTELSRIVER_OFFICIAL_LOGO_URL} alt="HotelsRiver" />
          <span className="hpm-pill">
            {hotelsOnly ? "מלון" : "טיסה + מלון"}
            {isLive ? " · חי" : " · דמו"}
          </span>
        </div>
        <BookingStageNav
          product={bookingProduct}
          activeId="passengers"
          onStage={(id) => {
            if (id === "results") (onResults || onBack)?.();
            else if (id === "detail") onBack?.();
          }}
        />
        <button type="button" className="hpm-back" onClick={onBack}>
          <LeftOutlined />
          חזרה לחדר
        </button>
      </header>

      <div className="hpm-layout">
        {awaitingRoomConfirm ? <RoomConfirmPageSkeleton /> : null}
        {awaitingRoomConfirm ? null : (
        <>
        <main className="hpm-main">
          {isLive && !prebookLoading && prebookError ? (
            <div className="usm-fh-prebook-status" role="alert">
              <span className="usm-fh-prebook-status--err">
                {prebookError}
                {isHubwayzSearchExpiredError(prebookError) ? (
                  <button
                    type="button"
                    className="hpm-btn-primary usm-fh-prebook-retry"
                    onClick={() => onExpiredSearch?.()}
                  >
                    חזרה לתוצאות וחיפוש מחדש
                  </button>
                ) : (
                  <button
                    type="button"
                    className="hrm-link usm-fh-prebook-retry"
                    onClick={() => runPrebook()}
                    disabled={prebookLoading}
                  >
                    נסה שוב
                  </button>
                )}
              </span>
            </div>
          ) : null}

          {proposalError && !hotelsOnly ? (
            <div
              className="usm-fh-prebook-status usm-fh-prebook-status--err"
              role="alert"
            >
              PROPOSAL: {proposalError}
            </div>
          ) : null}

          {isLive ? (
            <HubwayzPrebookConfirmPanel
              normalized={prebookNormalized}
              loading={prebookLoading}
              error={
                prebookError && !prebookLoading ? prebookError : null
              }
              showDetails={!hotelsOnly}
            />
          ) : hotelsOnly ? (
            <div className="usm-fh-prebook-status usm-fh-prebook-status--ok" role="status">
              <CheckCircleFilled aria-hidden />
              <div className="usm-fh-prebook-status__copy">
                <p className="usm-fh-prebook-status__title">
                  זמינות החדר אושרה – ממשיכים לפרטי הנוסעים
                </p>
                <p className="usm-fh-prebook-status__subtitle">
                  החדר זמין לתאריכים שבחרתם. מלאו את פרטי הנוסעים כדי להמשיך בהזמנה.
                </p>
              </div>
            </div>
          ) : null}

          {hotelsOnly ? (
            <HotelGuestDetails
              passengers={passengers}
              rooms={roomInfo?.rooms}
              roomName={bookingDraft?.roomName}
              renderGuest={renderPassengerCard}
              onCopy={handleCopyPassengers}
              onPaste={handlePastePassengers}
              pasteDisabled={!hasClipboard}
            />
          ) : (
          <CollapsibleSection
            id="passengers"
            title="פרטי הנוסעים"
            subtitle="מלאו את כל פרטי הנוסעים בדיוק כפי שמופיעים בדרכון"
          >
            <div className="hpm-pax-live">
              <div className="hpm-pax-toolbar">
                <button
                  type="button"
                  className="hpm-btn-secondary"
                  onClick={handleCopyPassengers}
                >
                  <CopyOutlined /> העתק נוסעים
                </button>
                <button
                  type="button"
                  className="hpm-btn-ghost"
                  onClick={handlePastePassengers}
                  disabled={!hasClipboard}
                  title={
                    !hasClipboard
                      ? "אין נתונים שמורים. העתק תחילה פרטי נוסעים."
                      : undefined
                  }
                >
                  הדבק נוסעים
                </button>
                {!hasClipboard ? (
                  <span className="hpm-note">
                    אין נתונים להדבקה. העתק תחילה.
                  </span>
                ) : (
                  <span className="hpm-note">
                    יש נתונים שמורים להדבקה מחבילה אחרת.
                  </span>
                )}
              </div>

              <div className="hpm-pax-rooms">
                <div className="hpm-pax-room">
                  <div className="hpm-pax-room__chrome">
                    <span className="hpm-pax-room__icon-badge" aria-hidden>
                      <FaBed className="hpm-pax-room__bed" />
                    </span>
                    <span className="hpm-pax-room__badge">{roomBadge}</span>
                  </div>
                  <div className="hpm-pax-room__title">
                    <strong>חדר 1</strong>
                  </div>
                  <div className="hpm-pax-room__rule" aria-hidden />
                  <div className="hpm-pax-list">
                    {passengers.map((p, idx) => renderPassengerCard(p, idx))}
                  </div>
                </div>
              </div>
            </div>
          </CollapsibleSection>
          )}

          {hotelsOnly ? (
            <>
              <HotelGuestStayDetails
                draft={bookingDraft}
                display={prebookNormalized?.display}
                rooms={roomInfo?.rooms}
              />
              <section className="hwz-hotel-notes hwz-hotel-notes--edit" aria-labelledby="hwz-hotel-agent-notes">
                <h2 id="hwz-hotel-agent-notes">הערות למלון</h2>
                <textarea
                  value={hotelNotes}
                  rows={4}
                  placeholder="הקלידו הערה למלון"
                  onChange={(e) => setHotelNotes(e.target.value)}
                />
              </section>
              <HotelGuestLegal remarks={prebookNormalized?.display?.remarks} />
            </>
          ) : (
            <CollapsibleSection
              id="baggage"
              title="הוספת כבודה"
              subtitle="בחרו כבודה לטיסה או המשיכו ללא תוספת — המחיר מתעדכן בסיכום"
            >
              <UnifiedHpmBaggagePurchase
                rawDeal={rawDeal}
                roomInfo={roomInfo}
                passengers={passengers}
                extras={extras}
                onTogglePax={handleTogglePax}
              />
            </CollapsibleSection>
          )}
        </main>

        <aside className="hpm-summary" aria-label="סיכום ההזמנה">
          {hotelsOnly ? (
            <HubwayzRoomAgentTools
              hotelId={bookingDraft?.hotelId}
              hotelName={bookingDraft?.hotelName}
              city={bookingDraft?.city}
              country={bookingDraft?.country}
              checkIn={appliedSearch?.checkIn || bookingDraft?.checkIn}
              checkOut={appliedSearch?.checkOut || bookingDraft?.checkOut}
              roomName={bookingDraft?.roomName}
              boardLabel={bookingDraft?.boardLabel}
              quote={baseQuote}
              displayCurrency={
                bookingDraft?.displayCurrency || appliedSearch?.currency || ""
              }
              cancellation={bookingDraft?.cancellationSummary || ""}
              nights={bookingDraft?.nights}
              onToast={showToast}
              initialCommissionIls={bookingDraft?.addedCommissionIls}
              onCustomerQuote={(priced) => {
                if (priced?.commissionLocked && priced.binding) {
                  setLockedCommissionQuote(priced);
                  setAddedCommissionIls(0);
                  return;
                }
                setLockedCommissionQuote(null);
                setAddedCommissionIls(priced?.ok ? priced.addedCommissionIls : 0);
              }}
            />
          ) : null}
          {hotelsOnly && pricing.priceChanged ? (
            <label className="hpm-terms" style={{ marginBottom: 12 }}>
              <input
                type="checkbox"
                checked={priceChangeAck}
                onChange={(e) => setPriceChangeAck(e.target.checked)}
              />
              <span>
                מחיר האישור שונה ממחיר החיפוש. הסיכום מציג את המחיר המעודכן. אני מאשר/ת את המחיר לפני המשך.
              </span>
            </label>
          ) : null}
          {hotelsOnly && customerPrice?.status === "price_changed" ? (
            <div role="status">
              <p>
                מחיר ההצעה עודכן. הנטו לסוכן הוא ₪{customerPrice.agentNetIls}, ומחיר המכירה ללקוח הוא ₪{customerPrice.customerSaleIls}.
              </p>
              <button
                type="button"
                onClick={() => passengerQuotes.acknowledge(bookingDraft?.hubwayzOfferCode)}
              >
                אישור המחיר המעודכן
              </button>
            </div>
          ) : null}
          <UnifiedFlightHotelOrderSummary
            hotelName={bookingDraft?.hotelName || prebookNormalized?.display?.hotelName || ""}
            city={bookingDraft?.city || ""}
            country={bookingDraft?.country || ""}
            checkIn={
              appliedSearch?.checkIn || bookingDraft?.checkIn || null
            }
            checkOut={
              appliedSearch?.checkOut || bookingDraft?.checkOut || null
            }
            dateRangeLabel={bookingDraft?.dateRangeLabel}
            nights={bookingDraft?.nights}
            roomsCount={
              isLive && prebookNormalized?.display
                ? prebookNormalized.display.roomsCount
                : Array.isArray(roomInfo?.rooms)
                  ? roomInfo.rooms.length
                  : 1
            }
            adultsCount={
              isLive && prebookNormalized?.display
                ? prebookNormalized.display.adultsTotal
                : Array.isArray(roomInfo?.rooms)
                  ? roomInfo.rooms.reduce(
                      (s, r) => s + (Number(r.adults) || 0),
                      0
                    )
                  : passengers.length
            }
            childrenCount={
              isLive && prebookNormalized?.display
                ? prebookNormalized.display.childrenTotal
                : Array.isArray(roomInfo?.rooms)
                  ? roomInfo.rooms.reduce(
                      (s, r) => s + (Number(r.kids) || 0),
                      0
                    )
                  : 0
            }
            roomName={
              bookingDraft?.roomName || prebookNormalized?.display?.roomSummary || null
            }
            boardLabel={displayBoardBasisHe(
              bookingDraft?.boardLabel || prebookNormalized?.display?.boardSummary || ""
            )}
            outbound={hotelsOnly ? null : summaryFlightLegs.outbound}
            inbound={hotelsOnly ? null : summaryFlightLegs.inbound}
            flightOptionLabel={
              hotelsOnly
                ? null
                : bookingDraft?.flightOptionLabel || bookingDraft?.flightLabel
            }
            totalFx={pricing.totalFx}
            currency={
              hotelsOnly
                ? bookingDraft?.displayCurrency || bookingDraft?.currency || pricing.currency
                : pricing.currency
            }
            displayCurrency={
              hotelsOnly
                ? bookingDraft?.displayCurrency || appliedSearch?.currency || null
                : null
            }
            customerPrice={customerPrice}
            pricePresentation={hotelsOnly ? "offer" : "payment"}
            baggageTotalFx={hotelsOnly ? 0 : pricing.baggageTotalFx}
            baggageItems={hotelsOnly ? [] : baggageDetailItems}
            baggageLabel={hotelsOnly ? null : baggageDetailLabel || null}
            termsChecked={termsChecked}
            onTermsChange={setTermsChecked}
            showTerms
            primaryDisabled={
              payBusy ||
              prebookLoading ||
              proposalChecking ||
              (isLive && !prebookNormalized?.supplierConfirmed)
            }
            primaryDisabledHint={
              isLive && !prebookLoading && !prebookNormalized?.supplierConfirmed
                ? hotelsOnly
                  ? "ממתינים לאישור זמינות החדר."
                  : "ממתינים לאישור Pre-Book מלא מהספק…"
                : null
            }
            primaryLabel={
              proposalChecking
                ? "בודק PROPOSAL…"
                : payBusy
                  ? "שומרים הזמנה…"
                : hotelsOnly
                  ? "תשלום"
                  : "המשך לתשלום"
            }
            onPrimary={() => handleContinue("pay_now")}
            deferredLabel={showDeferredPay ? "שריין ושלם אחר כך" : null}
            onDeferred={
              showDeferredPay ? () => handleContinue("pay_later") : null
            }
            secondaryLabel="חזרה לבחירת חדר"
            onSecondary={onBack}
          />
        </aside>
        </>
        )}
      </div>

      {awaitingRoomConfirm ? (
        <RoomConfirmModal
          open
          steps={roomConfirm.steps}
          failure={roomConfirm.failure}
          busy={prebookLoading || passengerQuotes.loading}
          searchExpired={isHubwayzSearchExpiredError(roomConfirm.failure?.message)}
          onRetry={() => runPrebook()}
          onAcknowledge={() => passengerQuotes.acknowledge(bookingDraft?.hubwayzOfferCode)}
          onExpiredSearch={onExpiredSearch}
        />
      ) : null}

      {toast ? (
        <div className="hpm-toast" role="status">
          {toast}
        </div>
      ) : null}
    </div>
  );
}

function RoomConfirmPageSkeleton() {
  return (
    <>
      <main className="hpm-main" aria-hidden="true">
        <section className="hpm-section usm-hwz-page-skel">
          <Skeleton active title={{ width: "38%" }} paragraph={{ rows: 1, width: "55%" }} />
          <Skeleton active title={false} paragraph={{ rows: 4, width: ["100%", "92%", "100%", "70%"] }} />
          <Skeleton active title={{ width: "28%" }} paragraph={{ rows: 3, width: ["100%", "86%", "64%"] }} />
        </section>
      </main>
      <aside className="hpm-summary" aria-hidden="true">
        <div className="hpm-summary__card usm-hwz-page-skel">
          <Skeleton active title={{ width: "46%" }} paragraph={{ rows: 5, width: ["80%", "100%", "90%", "70%", "60%"] }} />
          <Skeleton.Button active block style={{ height: 74, marginTop: 12 }} />
          <Skeleton.Button active block style={{ height: 40, marginTop: 8 }} />
        </div>
      </aside>
    </>
  );
}

function HotelGuestDetails({
  passengers,
  rooms,
  roomName,
  renderGuest,
  onCopy,
  onPaste,
  pasteDisabled,
}) {
  const groups = hotelGuestGroups(passengers, rooms);
  return (
    <section className="hpm-section" aria-labelledby="hwz-guests-title">
      <div className="hpm-section__head usm-fh-section__static-head">
        <div>
          <h2 id="hwz-guests-title">פרטי אורחי המלון</h2>
          <p>שם באנגלית, כפי בדרכון.</p>
        </div>
      </div>
      <div className="hpm-section__body">
        <div className="hpm-pax-toolbar">
          <button type="button" className="hpm-btn-secondary" onClick={onCopy}>
            <CopyOutlined /> העתק נוסעים
          </button>
          <button
            type="button"
            className="hpm-btn-ghost"
            onClick={onPaste}
            disabled={pasteDisabled}
          >
            הדבק נוסעים
          </button>
        </div>
        {groups.map((group, groupIndex) => (
          <div className="hpm-pax-room" key={`room-${groupIndex}`}>
            <div className="hpm-pax-room__chrome">
              <span className="hpm-pax-room__icon-badge" aria-hidden>
                <FaBed className="hpm-pax-room__bed" />
              </span>
              <span className="hpm-pax-room__badge">
                <bdi dir="ltr" lang="en">
                  {roomName || "חדר"}
                </bdi>
              </span>
            </div>
            <div className="hpm-pax-room__title">
              <strong>חדר {groupIndex + 1}</strong>
              <span>{roomGuestsLabel(group.room)}</span>
            </div>
            <div className="hpm-pax__form">
              {group.guests.map(({ p, idx }, local) => renderGuest(p, idx, local))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function StayFact({ label, value, ltr = false }) {
  if (!value) return null;
  return (
    <div>
      <dt>{label}</dt>
      <dd dir={ltr ? "ltr" : undefined}>{value}</dd>
    </div>
  );
}

function HotelGuestStayDetails({ draft, display, rooms }) {
  const adults = (rooms || []).reduce((sum, room) => sum + (Number(room.adults) || 0), 0);
  const children = (rooms || []).reduce(
    (sum, room) => sum + (Number(room.kids) || 0) + (Number(room.infants) || 0),
    0
  );
  const roomCount = Math.max(1, rooms?.length || 1);
  const occupancy = `${roomCount} חדר${roomCount === 1 ? "" : "ים"} · ${
    children > 0
      ? `${adults} מבוגרים, ${children} ילדים`
      : adults === 1
        ? "מבוגר אחד"
        : `${adults} מבוגרים`
  }`;
  const checkIn = formatStayDate(draft?.checkIn);
  const checkOut = formatStayDate(draft?.checkOut);
  const dates = checkIn && checkOut ? `${checkIn} – ${checkOut}` : checkIn || checkOut;
  const address =
    display?.addressLine ||
    [draft?.address, draft?.city, draft?.country].filter(Boolean).join(", ");
  const stars = Number(draft?.stars);
  const rating = Number.isFinite(stars) && stars > 0 ? `${stars} כוכבים` : null;

  return (
    <section className="usm-hwz-stay-summary" aria-label="פרטי המלון" dir="rtl">
      <h2>פרטי המלון</h2>
      <dl>
        <StayFact label="מלון" value={draft?.hotelName || display?.hotelName} ltr />
        <StayFact label="כתובת" value={address} />
        <StayFact label="דירוג" value={rating} />
        <StayFact label="חדר" value={draft?.roomName || display?.roomSummary} ltr />
        <StayFact label="סוג מיטה" value={draft?.beddingLabel} ltr />
        <StayFact
          label="בסיס אירוח"
          value={displayBoardBasisHe(draft?.boardLabel || display?.boardSummary || "")}
        />
        <StayFact label="תפוסה" value={occupancy} />
        <StayFact label="תאריכים" value={dates} ltr />
        <StayFact label="ביטול" value={draft?.cancellationSummary} />
      </dl>
    </section>
  );
}

function HotelGuestLegal({ remarks }) {
  const notes = (Array.isArray(remarks) ? remarks : [])
    .map((row) => hideSupplierBrandFromUserText(row?.text || row))
    .filter(Boolean);
  return (
    <div className="hwz-legal">
      <details>
        <summary>תקנון חברה</summary>
        <div className="hwz-legal__body">
          <p>
            ההזמנה כפופה לזמינות, לתנאי הספק, למדיניות הביטול ולתקנון החברה. פרטי המחיר, תנאי התשלום ותנאי הביטול המחייבים הם אלו שמופיעים בשובר ובתקנון.
          </p>
          <p>
            לתקנון המלא:{" "}
            <a href={HOTEL_GUEST_TERMS_URL} target="_blank" rel="noopener noreferrer">
              {HOTEL_GUEST_TERMS_URL}
            </a>
          </p>
        </div>
      </details>
      <details>
        <summary>גילוי נאות</summary>
        <div className="hwz-legal__body">
          <ul>
            {HOTEL_GUEST_DISCLOSURE.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      </details>
      {notes.length ? (
        <section className="hwz-hotel-notes" aria-labelledby="hwz-hotel-notes-title">
          <h2 id="hwz-hotel-notes-title">הערות המלון</h2>
          {notes.map((note) => (
            <p key={note}>{note}</p>
          ))}
        </section>
      ) : null}
    </div>
  );
}
